
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { DateTime } from 'luxon';

import { sharedState } from '../../../../../framework/state';
import { getEnumerationOptions, getResourceOptions, OptionValue } from '../../../../../framework/service/options';
import { dateToDbDateString } from '../../../../../../common/framework/util/convert';

import CurrencyAmountField from '../../../../../../browser/framework/fields/CurrencyAmountField.vue';
import TextField from '../../../../../../browser/framework/fields/TextField.vue';
import EmailField from '../../../../../../browser/framework/fields/EmailField.vue';
import DateField from '../../../../../../browser/framework/fields/DateField.vue';
import NumberField from '../../../../../../browser/framework/fields/NumberField.vue';
import SelectField from '../../../../../framework/fields/SelectField.vue';

import { VehicleContract } from '../../../../../../common/application/model/VehicleContract';

import { VehicleElectricalClass } from '../../../../../../common/application/model/enums/VehicleElectricalClass';
import { SmartFleetContractType } from '../../../../../../common/application/model/enums/SmartFleetContractType';
import { VehicleState } from '../../../../../../common/application/model/enums/VehicleState';
import { FinancingContractType } from '../../../../../../common/application/model/enums/FinancingContractType';
import { VehicleEnginePowerType } from '../../../../../../common/application/model/enums/VehicleEnginePowerType';
import { VehicleIntendedUse } from '../../../../../../common/application/model/enums/VehicleIntendedUse';
import { VehicleType } from '../../../../../../common/application/model/enums/VehicleType';

import { formatToFinnishDate } from '../../../../service/helper_service';
import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';
import {
    getCustomerOptions,
    getVendorOptions,
} from '../../../../../../browser/application/service/application_options_service';
import { importCustomerFleetFromTraficomCsv } from '../../../../../../browser/application/client/vehicle_contract_client';

import AutoCompleteMultipleField from '../../../../../application/fields/AutoCompleteMultipleField.vue';
import MonthsField from '../../../../../../browser/application/fields/MonthsField.vue';

@Component({
    components: {
        CurrencyAmountField,
        MonthsField,
        NumberField,
        DateField,
        TextField,
        SelectField,
        EmailField,
        AutoCompleteMultipleField,
    },
})
export default class ImportVehicleContractsTable extends Vue {
    @Prop(Boolean) readonly loading!: boolean;
    @Prop(Array) readonly traficomVehicles!: any[];

    shared = sharedState;
    local = {
        rows: [] as VehicleContract[],
        page: 1,
        checkedRows: [] as any[],
        selected: undefined as any | undefined,
        vehicleElectricalClassOptions: [] as OptionValue[],
        vehicleMakeOptions: [] as OptionValue[],
        vehicleStateOptions: [] as OptionValue[],
        financingContractTypeOptions: [] as OptionValue[],
        smartFleetContractTypeOptions: [] as OptionValue[],
        vehicleTypeOptions: [] as OptionValue[],
        vehicleEnginePowerTypeOptions: [] as OptionValue[],
        vehicleIntendedUseOptions: [] as OptionValue[],
        customerOptions: [] as OptionValue[],
        vendorOptions: [] as OptionValue[],
    };

    async mounted() {
        this.local.rows = [...this.traficomVehicles];
        this.local.vehicleElectricalClassOptions = getEnumerationOptions(
            'VehicleElectricalClass',
            Object.keys(VehicleElectricalClass),
            this,
        );
        this.local.vehicleStateOptions = getEnumerationOptions('VehicleState', Object.keys(VehicleState), this);
        this.local.smartFleetContractTypeOptions = getEnumerationOptions(
            'SmartFleetContractType',
            Object.keys(SmartFleetContractType),
            this,
        );
        this.local.financingContractTypeOptions = getEnumerationOptions(
            'FinancingContractType',
            Object.keys(FinancingContractType),
            this,
        );
        this.local.vehicleEnginePowerTypeOptions = getEnumerationOptions(
            'VehicleEnginePowerType',
            Object.keys(VehicleEnginePowerType),
            this,
        );
        this.local.vehicleIntendedUseOptions = getEnumerationOptions(
            'VehicleIntendedUse',
            Object.keys(VehicleIntendedUse),
            this,
        );

        this.local.vehicleTypeOptions = getEnumerationOptions('VehicleType', Object.keys(VehicleType), this);

        await this.loadAsyncData();
    }

    @Watch('traficomVehicles')
    vehicleRowsChanged() {
        this.local.rows = [...this.traficomVehicles];
    }

    async importFleet() {
        const result = await importCustomerFleetFromTraficomCsv(this.local.checkedRows);
        this.local.rows = [];

        this.$buefy.toast.open({
            message: this.$t('message.importedVehicles', [
                result.vehicleContracts.added.length,
                result.vehicleContracts.updated.length,
                result.vehicleContracts.failed.length,
            ]).toString(),
            type: 'is-primary',
            position: 'is-top',
            duration: 8000,
        });
    }

    setContractDatesAndExpensesFromFirstRow() {
        const smartFleetContractStart = this.local.rows[0].smartFleetContractStart;
        const smartFleetContractEnd = this.local.rows[0].smartFleetContractEnd;
        const smartFleetContractPeriodMonths = this.local.rows[0].smartFleetContractPeriodMonths;
        const smartFleetContractMonthlyFee = this.local.rows[0].smartFleetContractMonthlyFee;
        const smartFleetContractInitialFee = this.local.rows[0].smartFleetContractInitialFee;
        const financingContractType = this.local.rows[0].financingContractType;
        const financingContractFinancingVendorId = this.local.rows[0].financingContractFinancingVendorId;
        const financingContractMonthlyPayment = this.local.rows[0].financingContractMonthlyPayment;
        const financingContractStart = this.local.rows[0].financingContractStart;
        const financingContractEnd = this.local.rows[0].financingContractEnd;
        const financingContractPeriodMonths = this.local.rows[0].financingContractPeriodMonths;
        const updatedRows: VehicleContract[] = [];

        this.local.rows.forEach((row) => {
            updatedRows.push({
                ...row,
                financingContractType,
                financingContractFinancingVendorId,
                financingContractMonthlyPayment,
                financingContractStart,
                financingContractEnd,
                financingContractPeriodMonths,
                smartFleetContractStart,
                smartFleetContractEnd,
                smartFleetContractPeriodMonths,
                smartFleetContractInitialFee,
                smartFleetContractMonthlyFee,
            });
        });

        this.local.rows = [...updatedRows];
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    onRowClick(row: VehicleContract) {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }

    formatDate(date: string): string {
        return formatToFinnishDate(date);
    }

    isFirstRow(vehicleRegistrationNumber: string): boolean {
        return this.local.rows.findIndex((row) => row.vehicleRegistrationNumber === vehicleRegistrationNumber) === 0;
    }

    setSFEndDate(): void {
        if (this.loading) {
            return;
        }

        const updatedRows = [...this.local.rows];

        updatedRows[0] = {
            ...updatedRows[0],
            smartFleetContractEnd: this.calculateEndDate(
                updatedRows[0].smartFleetContractStart as any,
                updatedRows[0].smartFleetContractPeriodMonths,
            ),
        };

        this.local.rows = [...updatedRows];
    }

    setFinancingEndDate(): void {
        if (this.loading) {
            return;
        }

        const updatedRows = [...this.local.rows];

        updatedRows[0] = {
            ...updatedRows[0],
            financingContractEnd: this.calculateEndDate(
                updatedRows[0].financingContractStart as any,
                updatedRows[0].financingContractPeriodMonths,
            ),
        };

        this.local.rows = [...updatedRows];
    }

    calculateEndDate(start: string, duration: number): Date {
        return dateToDbDateString(
            DateTime.fromFormat(start, 'yyyy-MM-dd')
                .plus({ months: duration - 1 })
                .endOf('month')
                .minus({ hours: 12 })
                .toJSDate(),
        ) as any;
    }

    private async loadAsyncData(): Promise<void> {
        this.local.customerOptions = await getCustomerOptions();
        this.local.vehicleMakeOptions = await getResourceOptions(ApplicationResource.VEHICLE_MAKE, 'name');
        this.local.vendorOptions = await getVendorOptions();
    }
}
